<template>
    <div class="pa-5">
        <a @click="$router.push('/Meetings')">
            <v-icon class="mr-2">fas fa-chevron-left</v-icon>
            Back to Meetings
        </a>
       
        <div class="layout-title justify-space-between">
            <div class="layout-title--text">{{ meetingRec.title }}</div>
            <toolbar
                v-if="meetingRec"
                :meeting="meetingRec"
                :occurrences="occurrences"
                v-model="occurrence"
                @edit="editing = true"
                @delete="$emit('delete', meeting)"
            />
        </div>

        <v-tabs height="48px" v-model="activeTab" class="ec-collection-tabs px-4">
            <v-tab v-for="tab in menuItems" class="small-tab small-tab-all">{{ tab.name }}</v-tab>
        </v-tabs>

        <v-carousel
            class="pt-5"
            v-model="activeTab"
            height="100%"
            hide-delimiters light
            :show-arrows="false"
        >
            <v-carousel-item key="0">
                <div class="d-flex flex-column" style="height: calc(100vh - 300px); width: 100%;">
                    <agenda :key="agendaKey" :meeting="meetingRec" :occurrence="occurrence" @students="updateStudents" @navigate="navigateToTab" />
                </div>
            </v-carousel-item>
            <v-carousel-item key="1">
                <div class="d-flex flex-column" style="height: calc(100vh - 300px); width: 100%;">
                    <students :key="`meeting-${meetingRec.id}-students`" :meeting="meetingRec"/>
                </div>
            </v-carousel-item>
            <v-carousel-item key="2">
                <div class="d-flex flex-column" style="height: calc(100vh - 300px); width: 100%;">
                    <div class="headline mt-5 pl-5">Notes</div>
                    <message :key="`meeting-${meetingRec.id}-message`" dense class="ma-5" messageModel="meetingNotes" :params="{ dashboard_meeting_id: id}" @created="loadData" />
                </div>
            </v-carousel-item>
            <v-carousel-item key="3">
                <div class="d-flex flex-column" style="height: calc(100vh - 300px); width: 100%;">
                    <!-- since action items are iframed in and the component has aggressive margins/padding, handle body padding at the component level in here -->
                    <action-items :key="`meeting-${meetingRec.id}-action-items`" :urlParams="{dashboard_meeting_id: id, hide_menu: true }" style="margin: -16px;" fromMeetings @navigate="navigateToTab" />
                </div>
            </v-carousel-item>
        </v-carousel>
        <meeting-creation :key="`meeting-${meetingRec.id}-meeting-creation`" v-if="editing" :editing="meetingRec" @updated="handleMeetingUpdated" @closed="editing = false" @close="editing = false" />
    </div>
</template>

<script>
    import MeetingCreation from '../creation'
    import Message from '@/components/common/Message'
    import ActionItems from '@/components/modules/actionItems/ActionItems.vue'
    import Students from '@/components/modules/meetings/workspace/students'
    import Agenda from '@/components/modules/meetings/workspace/agenda'
    import Toolbar from '@/components/modules/meetings/workspace/Toolbar'

    export default {
        name: 'MeetingWorkspace',
        components: { Message, ActionItems, Students, Agenda, Toolbar, MeetingCreation },
        props: {
            id: {
                required: true,
            }
        },
        watch: {
            id(v) {
                this.activeItem = this.menuItems[0]
                this.activeTab = 0
                this.meetingRec = {}
                this.selectedOccurrence = null
                this.loadData()
            },
            meetingRec(newMeetingRec, oldMeetingRec) {
                // If a specific occurrence was selected, update it with fresh data from the new meetingRec
                if (this.selectedOccurrence && newMeetingRec?.occurrences) {
                    const updatedOccurrence = newMeetingRec.occurrences.find(occ => occ.id === this.selectedOccurrence.id)
                    if (updatedOccurrence) {
                        this.selectedOccurrence = Object.assign({}, updatedOccurrence, {
                            name: this.$dayjs(updatedOccurrence.occurrence, "YYYY-MM-DD").format("ddd M/D/YYYY"),
                            icon: 'far fa-calendar-alt'
                        })
                    }
                }
            }
        },
        data() {
            return {
                editing: false,
                activeItem: null,
                selectedOccurrence: null,
                meetingRec: {},
                menuItems: [{
                    id: 'agenda',
                    name: 'Agenda & Summary'
                }, {
                    id: 'students',
                    name: 'Students'
                }, {
                    id: 'notes',
                    name: 'Notes'
                }, {
                    id: 'actionitems',
                    name: 'Action Items'
                }],
                prevRoute: null,
                activeTab: 0,
                agendaRefreshCount: 0,
            }
        },
        computed: {
            occurrence: {
                get () { return this.selectedOccurrence || (this.occurrences && this.occurrences.length > 0 && this.occurrences[0]) || null },
                set (v) { this.selectedOccurrence = v }
            },
            occurrences () {
                return (this.meetingRec.occurrences)
                    ? this.meetingRec.occurrences.map(itm => Object.assign({}, itm, {
                        name: this.$dayjs(itm.occurrence, "YYYY-MM-DD").format("ddd M/D/YYYY"),
                        icon: 'far fa-calendar-alt'
                    }))
                    : []
            },
            agendaKey() {
                return `meeting-${this.meetingRec.id}-agenda-${this.meetingRec.form_id || 'none'}-${this.agendaRefreshCount}`
            }
        },
        mounted() {
            this.activeItem = this.menuItems[0]
            this.loadData()
        },
        methods: {
            navigateToTab(tabId) {
                const index = this.menuItems.findIndex(item => item.id === tabId)
                if (index !== -1) {
                    this.activeTab = index
                }
            },
            updateStudents (records) {
                this.meetingRec = Object.assign({}, this.meetingRec, {
                    dashboard_meeting_students: records
                })
            },
            handleMeetingUpdated() {
                // Reload the full meeting data, then remount the agenda tab so its
                // nested fe-crud instances autoload fresh data (rather than relying
                // on their config-diffing to detect a refetch is needed)
                this.loadData().then(() => {
                    this.agendaRefreshCount++
                })
            },
            loadData() {
                this.$setLoading(true)
                return this.$modelGet('meeting', { id: this.id })
                    .then(r => {
                        if (r.length === 1) {
                            this.meetingRec = r[0]
                        } else {
                            console.warn('Unable to uniquely identify meeting', r)
                        }
                    })
                    .finally(() => {
                        this.$setLoading(false)
                    })
            },
            navigate() {
                //  go to meetings or collections based on prevRoute
                this.$router.replace(this.prevRoute.path)
            }
        },
        beforeRouteEnter(to, from, next) {
            next(me => {
                me.prevRoute = from
            })
        }
    }
</script>

<style lang="scss" scoped>
.layout-title {
    padding-top: 20px;
    display: flex;
    align-items: center;
    &--text {
        font-size: 18px;
        font-weight: bold;
    }
}
.agenda-toolbar {
    position: absolute;
    height: 48px;
    right: 2px;
    top: 0;
    width: 500px;
    .is-collapsed & {
        top: 50px;
        left: 0;
        width: auto;
        padding-left: 20px;
        border-bottom: solid 1px #f5f5f5;
    }
}
</style>
